import {Icon, ToggleButtonGroup} from '@wandb/components';
import {Select} from '@wandb/weave/components/Form/Select';
import {TextField} from '@wandb/weave/components/Form/TextField';
import {Tooltip} from '@wandb/weave/components/Tooltip';
import React, {useEffect, useRef, useState} from 'react';

import {ErrorText, SeparatedChunk} from '../../shared';
import {
  aggregationFunctionOptions,
  DEFAULT_WINDOW_SIZE,
  MAX_WINDOW_SIZE,
  timeUnitOptions,
} from '../constants';
import {
  type AggregationFunction,
  type AggregationFunctionOption,
  type MetricType,
  type TimeUnit,
  type TimeUnitOption,
} from '../types';
import {convertSecondsToTimeUnit, convertTimeToSeconds} from '../utils';

type WindowConfigProps = {
  windowSize?: number;
  windowDuration?: number;
  aggregationFunction?: AggregationFunction;
  onWindowSizeChange: (value: number) => void;
  onWindowDurationChange: (value: number) => void;
  onAggregationFunctionChange: (value: AggregationFunction) => void;
  hasCountError: boolean;
  hasTimeError: boolean;
  metricType?: MetricType;
  // Aggregation choices; spans expose only mean/min/max.
  aggregationOptions?: AggregationFunctionOption[];
  // When true, only the time-based window is offered (spans).
  durationOnly?: boolean;
};

export const WindowConfig = ({
  windowSize,
  windowDuration,
  aggregationFunction = 'mean',
  onWindowSizeChange,
  onWindowDurationChange,
  onAggregationFunctionChange,
  hasCountError,
  hasTimeError,
  metricType,
  aggregationOptions = aggregationFunctionOptions,
  durationOnly = false,
}: WindowConfigProps) => {
  const windowType =
    durationOnly || windowDuration ? 'TIME_BASED' : 'COUNT_BASED';

  // Preserve the user's count-based window size across time-based toggles
  const savedCountWindowSize = useRef<number>(
    windowSize != null && windowSize > 0 && !windowDuration
      ? windowSize
      : DEFAULT_WINDOW_SIZE
  );

  const [displayWindowSize, setDisplayWindowSize] = useState<string>(
    windowSize != null && windowSize > 0 ? windowSize.toString() : '1'
  );
  const [timeUnit, setTimeUnit] = useState<TimeUnit>('MINUTES');
  const [displayTimeValue, setDisplayTimeValue] = useState<string>(
    windowDuration != null && windowDuration > 0
      ? Math.round(
          convertSecondsToTimeUnit(windowDuration, 'MINUTES')
        ).toString()
      : '5'
  );

  useEffect(() => {
    if (windowSize && windowSize > 0) {
      setDisplayWindowSize(windowSize.toString());
    }
  }, [windowSize]);

  useEffect(() => {
    if (windowDuration && windowDuration > 0) {
      const convertedValue = Math.round(
        convertSecondsToTimeUnit(windowDuration, timeUnit)
      );
      setDisplayTimeValue(convertedValue.toString());
    }
  }, [windowDuration, timeUnit]);

  const handleWindowSizeChange = (value: string) => {
    setDisplayWindowSize(value);
    const numericValue = parseInt(value, 10);
    if (!isNaN(numericValue) && numericValue > 0) {
      onWindowSizeChange(numericValue);
      // Track user's count-based input so we can restore after time-based toggle
      if (!windowDuration) {
        savedCountWindowSize.current = numericValue;
      }
    } else {
      onWindowSizeChange(0);
    }
  };

  const handleTimeValueChange = (value: string) => {
    setDisplayTimeValue(value);
    const numericValue = parseInt(value, 10);
    if (!isNaN(numericValue) && numericValue > 0) {
      const durationInSeconds = convertTimeToSeconds(numericValue, timeUnit);
      onWindowDurationChange(durationInSeconds);
    } else {
      onWindowDurationChange(0);
    }
  };

  const handleTimeUnitChange = (unit: TimeUnit) => {
    setTimeUnit(unit);
    const numericValue = parseInt(displayTimeValue, 10);
    if (!isNaN(numericValue) && numericValue > 0) {
      const durationInSeconds = convertTimeToSeconds(numericValue, unit);
      onWindowDurationChange(durationInSeconds);
    }
  };

  const handleWindowTypeChange = (value: string) => {
    if (value === 'COUNT_BASED') {
      onWindowDurationChange(0);
      const restored = savedCountWindowSize.current;
      setDisplayWindowSize(restored.toString());
      onWindowSizeChange(restored);
    } else {
      // Save the current count-based window size before switching
      if (windowSize && windowSize > 0) {
        savedCountWindowSize.current = windowSize;
      }
      // Backend caps calls considered regardless of time window
      onWindowSizeChange(MAX_WINDOW_SIZE);
      setTimeUnit('MINUTES');
      setDisplayTimeValue('5');
      onWindowDurationChange(300);
    }
  };

  const currentAggOption =
    aggregationOptions.find(opt => opt.value === aggregationFunction) ?? null;

  // Status/error-rate metrics own their aggregation picker in the metric config.
  const aggregationInMetricConfig =
    metricType === 'status' || metricType === 'span_status';

  return (
    <SeparatedChunk>
      <div className="flex items-center gap-4">
        <div className="font-semibold">Window</div>
        <Tooltip
          content={
            windowType === 'COUNT_BASED'
              ? 'Number of recent calls to consider (1-100)'
              : 'Time window for metric aggregation'
          }
          trigger={<Icon icon="info" />}
        />
      </div>

      {!durationOnly && (
        <div className="mt-4 mb-8">
          <ToggleButtonGroup
            aria-label="Window type"
            items={[
              {id: 'COUNT_BASED', children: 'Count'},
              {id: 'TIME_BASED', children: 'Time'},
            ]}
            selectedKeys={[windowType]}
            selectionMode="single"
            disallowEmptySelection
            size="md"
            onSelectionChange={keys => {
              const next = Array.from(keys)[0];
              if (typeof next === 'string' && next !== windowType) {
                handleWindowTypeChange(next);
              }
            }}
          />
        </div>
      )}

      <div className="mb-8">
        {windowType === 'COUNT_BASED' ? (
          <>
            <div className="flex items-center gap-8">
              <span>Last</span>
              <div className="w-64">
                <TextField
                  ariaLabel="Window size"
                  value={displayWindowSize}
                  onChange={handleWindowSizeChange}
                  placeholder="Ex. 10"
                />
              </div>
              <span>calls</span>
            </div>
            {hasCountError && (
              <ErrorText>Window size must be between 1 and 100</ErrorText>
            )}
          </>
        ) : (
          <>
            <div className="flex items-center gap-8">
              <span>Across the last</span>
              <div className="w-64">
                <TextField
                  ariaLabel="Time value"
                  value={displayTimeValue}
                  onChange={handleTimeValueChange}
                  placeholder="Ex. 5"
                />
              </div>
              <div>
                <Select<TimeUnitOption>
                  value={
                    timeUnitOptions.find(opt => opt.value === timeUnit) ?? null
                  }
                  onChange={option =>
                    handleTimeUnitChange(option?.value || 'MINUTES')
                  }
                  options={timeUnitOptions}
                />
              </div>
            </div>
            <div className="mt-8 text-sm text-moon-600 dark:text-wb-text-secondary">
              Note: only (at most) the last 100 calls are considered regardless
              of window size
            </div>
            {hasTimeError && (
              <ErrorText>
                Time window must be a positive number and not exceed 24 hours
              </ErrorText>
            )}
          </>
        )}
      </div>

      {/* Aggregation is handled by the metric config for status/error-rate */}
      {!aggregationInMetricConfig && (
        <div className="mt-16">
          <div className="flex items-center gap-4">
            <div className="font-semibold">Aggregation</div>
            <Tooltip
              content="Aggregation function applied to metric values within the window before threshold comparison"
              trigger={<Icon icon="info" />}
            />
          </div>
          <div className="mt-4">
            <Select<AggregationFunctionOption>
              value={currentAggOption}
              onChange={option =>
                onAggregationFunctionChange(option?.value || 'mean')
              }
              options={aggregationOptions}
              menuPlacement="auto"
            />
          </div>
        </div>
      )}
    </SeparatedChunk>
  );
};
